import { InjectionToken } from '@angular/core';
import { Action, ActionReducerMap, combineReducers, createReducer, on } from '@ngrx/store';
import { EncountersActions } from '@mona/pdms/data-access-encounters';
import { EntriesInterval } from '@mona/shared/date';
import { makeEntitiesReducer } from '@mona/store';
import {
    AllergiesState,
    AnamnesisState,
    BalanceState,
    BalanceTableEnum,
    CareReportsState,
    DiagnosesState,
    EncounterFeatureState,
    PhysicalExaminationState,
    PreMedicationsState,
} from '../../entities';
import {
    AllergiesAction,
    AnamnesisAction,
    BalanceActions,
    CareReportsAction,
    DiagnosesAction,
    EncounterAction,
    PhysicalExaminationAction,
    PreMedicationsAction,
} from '../actions';
import { additionalDeviceReducer } from './additional-device.reducer';
import { balanceLoadingReducer, balanceTargetReducer, balanceValuesReducer } from './balance.reducer';
import { basicCareProceduresMapReducer } from './basic-care-procedures-map.reducer';
import { careReportsEndDateReducer } from './care-reports-end-date.reducer';
import { careReportsIntervalReducer } from './care-reports-interval.reducer';
import { dateShiftEntryControlsReducer, entryControlFormReducer } from './date-shift-entry-controls.reducer';
import { historyFilterReducer } from './history-filter.reducer';
import { infectionStatusReducer } from './infection-status.reducer';
import { labValuesReducer } from './lab-values.reducer';
import { medicationAdministrationReducer } from './medication-administration.reducer';
import { outputsMapReducer } from './outputs-map.reducer';
import { selectedDateReducer } from './selected-date.reducer';
import { surgeryPrescriptionReducer } from './surgery-prescription.reducer';
import { therapyLimitationsReducer } from './therapy-limitations.reducer';
import { vaccinationStatusReducer } from './vaccinations.reducer';
import { valuablesReducer } from './valuables.reducer';
import { ventilationReducer } from './ventilation.reducer';
import { vitalSignsReducer } from './vital-signs.reducer';
import { woundStatusReducer } from './wound-status.reducer';

// =============================================================================
// ENCOUNER DATA REDUCER - MAIN REDUCER
// =============================================================================

/**
 * Allergies reducers
 */
const allergiesReducerMap: ActionReducerMap<AllergiesState> = {
    entities: makeEntitiesReducer(AllergiesAction.loadAllergiesAction, false, null, EncountersActions.clearEncounter),
};

/**
 * Anamnesis reducers
 */
const anamnesisReducerMap: ActionReducerMap<AnamnesisState> = {
    entities: makeEntitiesReducer(AnamnesisAction.loadAnamnesisAction, false, null, EncountersActions.clearEncounter),
};

/**
 * Diagnoses reducers
 */
const diagnosesReducerMap: ActionReducerMap<DiagnosesState> = {
    entities: makeEntitiesReducer(DiagnosesAction.loadDiagnosesAction, false, null, EncountersActions.clearEncounter),
};

/**
 * Pre medications reducers
 */
const preMedicationsReducerMap: ActionReducerMap<PreMedicationsState> = {
    entities: makeEntitiesReducer(
        PreMedicationsAction.loadPreMedicationsAction,
        false,
        null,
        EncountersActions.clearEncounter,
    ),
};

/**
 * Physical examination reducers
 */
const physicalExaminationReducerMap: ActionReducerMap<PhysicalExaminationState> = {
    entities: makeEntitiesReducer(
        PhysicalExaminationAction.loadPhysicalExaminationAction,
        false,
        null,
        EncountersActions.clearEncounter,
    ),
};

// The medication-administration reducer, its EntityAdapter and initial state were
// extracted into ./medication-administration.reducer.ts so the load-success path can
// be audited, unit-tested and migrated in isolation — matching the other
// reconcile-target reducers (vital-signs, lab-values, procedures, etc.).

/**
 * Balance reducers
 */
const balanceReducerMap: ActionReducerMap<BalanceState> = {
    interval: createReducer(
        EntriesInterval.HOUR_1 as EntriesInterval,
        on(BalanceActions.setIntervalAction, (state, { interval }) => interval),
    ),
    activeTable: createReducer(
        BalanceTableEnum.Inputs as BalanceTableEnum,
        on(BalanceActions.setActiveTable, (state, { idx }) => idx),
    ),

    isLoading: balanceLoadingReducer,
    outputs: makeEntitiesReducer(BalanceActions.loadOutputsAction),
    outputsMap: outputsMapReducer,
    balanceValues: balanceValuesReducer,
    balanceTargets: balanceTargetReducer,
    medicationAdministrations: medicationAdministrationReducer,
};

/**
 * Care reports reducers
 */
const careReportsReducerMap: ActionReducerMap<CareReportsState> = {
    interval: careReportsIntervalReducer,
    basicCareProcedures: makeEntitiesReducer(
        CareReportsAction.loadBasicCareProceduresAction,
        false,
        null,
        EncountersActions.clearEncounter,
    ),
    basicCareProceduresMap: basicCareProceduresMapReducer,
    entryControls: makeEntitiesReducer(
        CareReportsAction.loadEntryControlsAction,
        false,
        null,
        EncountersActions.clearEncounter,
    ),
    dateShiftEntryControls: dateShiftEntryControlsReducer,
    endDate: careReportsEndDateReducer,
    entryControlForm: entryControlFormReducer,
    isLoading: createReducer(
        false,
        on(CareReportsAction.loadBasicCareProceduresAction.action, (): boolean => true),
        on(CareReportsAction.loadBasicCareProceduresAction.succeededAction, (): boolean => false),
        on(CareReportsAction.loadBasicCareProceduresAction.failedAction, (): boolean => false),
    ),
};

/**
 * Encounter feature reducer map
 */
export const encounterFeatureReducer: ActionReducerMap<EncounterFeatureState> = {
    selectedDate: selectedDateReducer,
    encounterHistory: makeEntitiesReducer(
        EncounterAction.loadEncounterHistoryAction,
        false,
        null,
        EncountersActions.clearEncounter,
    ),
    encounterHistoryFilter: historyFilterReducer,
    isReviewMode: createReducer(
        false,
        on(EncounterAction.setEncounterReviewMode, (): boolean => true),
        on(EncounterAction.unsetEncounterReviewMode, EncountersActions.clearEncounter, (): boolean => false),
    ),
    additionalDevices: additionalDeviceReducer,
    allergies: combineReducers(allergiesReducerMap),
    infections: infectionStatusReducer,
    vaccinations: vaccinationStatusReducer,
    valuables: valuablesReducer,
    woundStatus: woundStatusReducer,
    therapyLimitations: therapyLimitationsReducer,
    surgeryPrescription: surgeryPrescriptionReducer,
    anamnesis: combineReducers(anamnesisReducerMap),
    diagnoses: combineReducers(diagnosesReducerMap),
    preMedications: combineReducers(preMedicationsReducerMap),
    physicalExamination: combineReducers(physicalExaminationReducerMap),
    vitalSigns: vitalSignsReducer,
    ventilation: ventilationReducer,
    labValues: labValuesReducer,
    balance: combineReducers(balanceReducerMap),
    careReports: combineReducers(careReportsReducerMap),
};

/**
 * Reducer injection token definition
 */
export const ENCOUNTER_FEATURE_REDUCER = new InjectionToken<ActionReducerMap<EncounterFeatureState, Action>>(
    'ENCOUNTER_FEATURE_REDUCER',
);

export * from './balance.reducer';
export * from './lab-values.reducer';
export * from './medication-administration.reducer';
export * from './vital-signs.reducer';
